import React, { useState, useEffect, useRef, useMemo } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
  FaIdCard,
  FaPalette,
  FaExclamationTriangle,
  FaUpload,
  FaBuilding,
  FaPlus,
  FaTrash,
  FaSave,
  FaFilePdf,
  FaSpinner,
  FaKey,
  FaCheckCircle,
  FaUserTie,
  FaGlobe,
  FaHeading,
  FaAlignLeft,
  FaLock,
  FaUnlock,
  FaSignOutAlt,
  FaFileInvoiceDollar,
  FaHourglassHalf,
} from 'react-icons/fa';
import './SettingsModal.css';
import BillingSettingsTab from './BillingSettingsTab';
import TrialCountdown from './TrialCountdown';
import { utcToSpInput, spInputToUtcIso } from '../utils/datetime';

// ─── UTILITÁRIOS ────────────────────────────────────────────────────────────
// (obs: os separadores de seção abaixo usam apenas o caractere "─", que é
// puramente decorativo em comentário e não interfere no parser)

const safeJsonParse = (data) => {
  if (!data) return {};
  if (typeof data === 'object') return data;
  try { return JSON.parse(data); } catch { return {}; }
};

const formatSlug = (val) =>
  (val || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');

const emitTenantConfigUpdate = (payload) => {
  window.dispatchEvent(new CustomEvent('tenant:config:updated', { detail: payload }));
};

const formatImageUrl = (url) => {
  if (!url || typeof url !== 'string') return null;

  if (/^(https?:|data:|blob:)/.test(url)) return url;

  const rawBase =
    api.defaults?.baseURL ||
    process.env.REACT_APP_API_URL ||
    process.env.REACT_APP_API_BASE_URL ||
    '';

  const base = rawBase
    ? rawBase.replace(/\/api\/?$/, '').replace(/\/$/, '')
    : window.location.origin;

  const cleanPath = url.replace(/^\//, '');
  return `${base}/${cleanPath}`;
};

const formatDateBR = (date) => {
  if (!date) return '-';
  try { return new Date(date).toLocaleDateString('pt-BR'); } catch { return '-'; }
};

// ─── COMPONENTES AUXILIARES ─────────────────────────────────────────────────

function InfoPill({ label, value, tone = 'default' }) {
  return (
    <div className={`sm-pill sm-pill--${tone}`}>
      <span className="sm-pill__label">{label}</span>
      <strong className="sm-pill__value">{value}</strong>
    </div>
  );
}

function ImageUploadBox({ label, hint, existingUrl, selectedFile, onSelect, onClear }) {
  const inputRef = useRef(null);
  const [localPreview, setLocalPreview] = useState(null);

  useEffect(() => {
    if (!selectedFile) { setLocalPreview(null); return; }
    const url = URL.createObjectURL(selectedFile);
    setLocalPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  const displayUrl = localPreview || formatImageUrl(existingUrl) || null;

  return (
    <div className="sm-image-box">
      <div className="sm-image-box__header">
        <span className="sm-field-label">{label}</span>
        {hint && <span className="sm-field-hint">{hint}</span>}
      </div>

      <div
        className={`sm-image-preview ${displayUrl ? 'has-image' : ''}`}
        onClick={() => inputRef.current?.click()}
      >
        <input
          type="file"
          ref={inputRef}
          style={{ display: 'none' }}
          accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
          onChange={(e) => {
            if (e.target.files?.[0]) onSelect(e.target.files[0]);
            e.target.value = null;
          }}
        />

        {displayUrl ? (
          <>
            <img
              src={displayUrl}
              alt={label}
              className="sm-image-thumb"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling?.classList?.remove('hidden');
              }}
            />
            <div className="sm-image-overlay">
              <FaUpload />
              Substituir
            </div>
            <button
              type="button"
              className="sm-image-clear"
              title="Remover imagem"
              onClick={(e) => { e.stopPropagation(); onClear(); }}
            >
              ✕
            </button>
          </>
        ) : (
          <div className="sm-image-placeholder">
            <FaUpload className="sm-image-placeholder-icon" />
            <span>Clique para selecionar</span>
            <span className="sm-image-placeholder-sub">PNG, JPG, SVG. Recomendado 400x400px</span>
          </div>
        )}
      </div>
    </div>
  );
}

function LicenseBar({ used, total }) {
  const pct = total > 0 ? Math.min((used / total) * 100, 100) : 0;
  const color = pct >= 100 ? '#dc2626' : pct >= 85 ? '#f59e0b' : '#7c3aed';

  return (
    <div className="sm-license-bar-wrap">
      <div className="sm-license-bar-track">
        <div className="sm-license-bar-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="sm-license-bar-label" style={{ color }}>
        {used}/{total} ({Math.round(pct)}%)
      </span>
    </div>
  );
}

// ─── COMPONENTE PRINCIPAL ───────────────────────────────────────────────────

export default function SettingsModal({ client, onClose, onSaveSuccess }) {
  const [activeTab, setActiveTab] = useState('geral');
  const [isSaving, setIsSaving] = useState(false);
  const [isActioning, setIsActioning] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);

  // ── [TRIAL] Período de teste do CLIENTE (prevalece sobre o unitário) ──────
  const [trialEnabled, setTrialEnabled]           = useState(false);
  const [trialExpiresLocal, setTrialExpiresLocal] = useState(''); // datetime-local (horário SP)

  // GERAL
  const [nomeEmpresa, setNomeEmpresa] = useState(client?.nome_empresa || '');
  const [documentoEmpresa, setDocumentoEmpresa] = useState(client?.documento || '');
  const [statusEmpresa, setStatusEmpresa] = useState(client?.status || 'ativo');
  const [contatos, setContatos] = useState([]);
  const [contratoFile, setContratoFile] = useState(null);

  // LICENÇAS
  const [maxLicencas, setMaxLicencas] = useState(client?.max_licencas || 5);
  const [licencasAlocadas, setLicencasAlocadas] = useState([]);

  // WHITE-LABEL
  const [slug, setSlug] = useState(client?.slug || '');
  const [sidebarLabel, setSidebarLabel] = useState('');
  const [dashboardTitle, setDashboardTitle] = useState('');
  const [dashboardText, setDashboardText] = useState('');

  const [existingLogoUrl, setExistingLogoUrl] = useState(null);
  const [existingLoginBg, setExistingLoginBg] = useState(null);

  const [logoFile, setLogoFile] = useState(null);
  const [loginBgFile, setLoginBgFile] = useState(null);

  const [clearLogo, setClearLogo] = useState(false);
  const [clearLoginBg, setClearLoginBg] = useState(false);

  const [logoPreviewSrc, setLogoPreviewSrc] = useState(null);
  const [bgPreviewSrc, setBgPreviewSrc] = useState(null);

  // CONTRATO ASSINADO (ClickSign)
  const [isRecapturando, setIsRecapturando] = useState(null);

  // Preview do contrato assinado (evita abrir <a href> direto, que perde o header de auth)
  const [contractPreviewUrl, setContractPreviewUrl] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isDeletingContract, setIsDeletingContract] = useState(false);

  const licEmUso = client?.licencas_em_uso || 0;

  const displayLogoSrc = logoPreviewSrc || formatImageUrl(clearLogo ? null : existingLogoUrl);
  const displayBgSrc   = bgPreviewSrc   || formatImageUrl(clearLoginBg ? null : existingLoginBg);

  const headerStatusText  = statusEmpresa === 'ativo' ? 'Ativo' : 'Suspenso';
  const previewCompanyName    = sidebarLabel || nomeEmpresa || client?.nome_empresa || 'Nome da Empresa';
  const previewDashboardTitle = dashboardTitle || 'Sobre a Empresa';
  const previewDashboardText  =
    dashboardText ||
    'Descreva aqui a empresa, seus diferenciais, serviços principais e missão. Este texto será exibido para os usuários no dashboard.';

  const headerMeta = useMemo(
    () => [
      { label: 'Tenant',   value: `#${client?.tenant_id ?? client?.id ?? '-'}` },
      { label: 'Status',   value: headerStatusText },
      { label: 'Cadastro', value: formatDateBR(client?.data_criacao) },
    ],
    [client?.id, client?.tenant_id, client?.data_criacao, headerStatusText],
  );

  const isFaturamentoTab = activeTab === 'faturamento';

  // Contrato veio automaticamente da ClickSign (webhook) ou foi anexado à mão?
  const contratoAssinadoViaClicksign = !!client?.contrato_url && client?.contrato_origem === 'clicksign';

  useEffect(() => {
    if (!logoFile) { setLogoPreviewSrc(null); return; }
    const url = URL.createObjectURL(logoFile);
    setLogoPreviewSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [logoFile]);

  useEffect(() => {
    if (!loginBgFile) { setBgPreviewSrc(null); return; }
    const url = URL.createObjectURL(loginBgFile);
    setBgPreviewSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [loginBgFile]);

  useEffect(() => {
    setNomeEmpresa(client?.nome_empresa || '');
    setDocumentoEmpresa(client?.documento || '');
    setStatusEmpresa(client?.status || 'ativo');
    setMaxLicencas(client?.max_licencas || 5);
    setSlug(client?.slug || '');

    const sidebarCfg = safeJsonParse(client?.sidebar_config);
    const dashCfg    = safeJsonParse(client?.dashboard_config);
    const loginCfg   = safeJsonParse(client?.login_config);

    setSidebarLabel(sidebarCfg.label || client?.nome_empresa || '');
    setDashboardTitle(dashCfg.title || '');
    setDashboardText(dashCfg.text  || '');

    setExistingLogoUrl(sidebarCfg.logo_url || loginCfg.logo_url || null);
    setExistingLoginBg(loginCfg.background_url || null);

    setLogoFile(null);
    setLoginBgFile(null);
    setClearLogo(false);
    setClearLoginBg(false);
    setContratoFile(null);
    setContatos([]);
    // [TRIAL] zera até a config carregar (evita vazar estado entre clientes)
    setTrialEnabled(false);
    setTrialExpiresLocal('');

    // Fecha qualquer preview aberto de um contrato do cliente anterior
    if (contractPreviewUrl) {
      URL.revokeObjectURL(contractPreviewUrl);
      setContractPreviewUrl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  useEffect(() => {
    if (!client?.id) return;

    const ctrl = new AbortController();

    const loadAll = async () => {
      setIsLoadingConfig(true);

      try {
        const [contatosRes, licencasRes] = await Promise.allSettled([
          api.get(`/api/system-hub/clientes/${client.id}/contatos`, { signal: ctrl.signal }),
          api.get(`/api/usuarios/licencas/cliente/${client.id}`,    { signal: ctrl.signal }),
        ]);

        if (contatosRes.status === 'fulfilled') {
          setContatos(contatosRes.value.data || []);
        }

        if (licencasRes.status === 'fulfilled') {
          setLicencasAlocadas(licencasRes.value.data || []);
        }

        try {
          const { data } = await api.get(
            `/api/system-hub/clientes/${client.id}/config`,
            { signal: ctrl.signal },
          );
          const sc = data.sidebar_config   || {};
          const dc = data.dashboard_config || {};
          const lc = data.login_config     || {};

          setSidebarLabel(sc.label || data.nome_empresa || client.nome_empresa || '');
          setDashboardTitle(dc.title || '');
          setDashboardText(dc.text  || '');
          setExistingLogoUrl(sc.logo_url || lc.logo_url || null);
          setExistingLoginBg(lc.background_url || null);
          // [TRIAL] estado do trial do cliente (trial_expires_at chega em ISO UTC)
          setTrialEnabled(!!data.trial_enabled);
          setTrialExpiresLocal(utcToSpInput(data.trial_expires_at)); // '' se null
        } catch {
          // Endpoint ainda não existe ou falhou. Mantém os dados do prop já carregados acima.
        }
      } finally {
        if (!ctrl.signal.aborted) setIsLoadingConfig(false);
      }
    };

    loadAll();
    return () => ctrl.abort();
  }, [client?.id, client?.nome_empresa]);

  const handleAddContato = () => {
    setContatos((prev) => [
      ...prev,
      {
        id: `temp_${Date.now()}`,
        nome: '', email: '', telefone: '', documento: '',
        cargo_tag: 'Admin', is_principal: false, is_billing: false,
      },
    ]);
  };

  const handleRemoveContato = async (index, contatoId) => {
    if (typeof contatoId === 'number') {
      try {
        await api.delete(`/api/system-hub/clientes/${client.id}/contatos/${contatoId}`);
      } catch {
        toast.error('Falha ao remover contato.');
        return;
      }
    }
    setContatos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleChangeContato = (index, field, value) => {
    setContatos((prev) => {
      const next = [...prev];
      if (field === 'is_principal' && value) next.forEach((c) => { c.is_principal = false; });
      if (field === 'is_billing'   && value) next.forEach((c) => { c.is_billing   = false; });
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (isFaturamentoTab) return;

    if (maxLicencas < licEmUso) {
      toast.warn(`Limite não pode ser menor que as licenças em uso (${licEmUso}).`);
      return;
    }

    // [TRIAL] valida data futura quando o trial do cliente está ativo
    if (trialEnabled) {
      const iso = spInputToUtcIso(trialExpiresLocal);
      if (!iso || new Date(iso).getTime() <= Date.now()) {
        toast.warn('Defina uma data/hora futura para o término do período de teste.');
        return;
      }
    }

    setIsSaving(true);

    try {
      await api.put(`/api/system-hub/clientes/${client.id}`, {
        nome_empresa:  nomeEmpresa,
        documento:     documentoEmpresa,
        status:        statusEmpresa,
        slug,
        max_licencas:  maxLicencas,
        // [TRIAL] cliente é o "maior decisor"
        trial_enabled:    trialEnabled,
        trial_expires_at: trialEnabled ? spInputToUtcIso(trialExpiresLocal) : null,
      });

      for (const contato of contatos) {
        if (typeof contato.id === 'string' && contato.id.startsWith('temp_')) {
          await api.post(`/api/system-hub/clientes/${client.id}/contatos`, contato);
        } else if (typeof contato.id === 'number') {
          await api.put(`/api/system-hub/clientes/${client.id}/contatos/${contato.id}`, contato);
        }
      }

      if (contratoFile) {
        const fdContrato = new FormData();
        fdContrato.append('contrato', contratoFile);
        await api.post(`/api/system-hub/clientes/${client.id}/contrato`, fdContrato);
      }

      const fd = new FormData();
      if (sidebarLabel  !== undefined) fd.append('sidebarLabel',   sidebarLabel);
      if (dashboardTitle !== undefined) fd.append('dashboardTitle', dashboardTitle);
      if (dashboardText  !== undefined) fd.append('dashboardText',  dashboardText);
      if (clearLogo)    fd.append('clearLogo',    'true');
      if (clearLoginBg) fd.append('clearLoginBg', 'true');
      if (logoFile)     fd.append('logo',         logoFile);
      if (loginBgFile)  fd.append('loginImage',   loginBgFile);

      const hasPayload =
        sidebarLabel !== '' || dashboardTitle !== '' || dashboardText !== '' ||
        !!logoFile || !!loginBgFile || clearLogo || clearLoginBg;

      if (hasPayload) {
        const { data } = await api.post(`/api/system-hub/clientes/${client.id}/customize`, fd);

        const persistedLogo = data?.sidebar_config?.logo_url ?? data?.login_config?.logo_url ?? null;
        const persistedBg   = data?.login_config?.background_url ?? null;

        setExistingLogoUrl(persistedLogo);
        setExistingLoginBg(persistedBg);
        setLogoFile(null);
        setLoginBgFile(null);
        setClearLogo(false);
        setClearLoginBg(false);

        emitTenantConfigUpdate({
          nome_empresa: data?.sidebar_config?.label || sidebarLabel || nomeEmpresa,
          logo_url: persistedLogo,
        });
      } else {
        emitTenantConfigUpdate({ nome_empresa: sidebarLabel || nomeEmpresa });
      }

      toast.success('Tenant atualizado com sucesso! ✅');
      onSaveSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Falha ao salvar. Verifique os dados e tente novamente.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRecapturarContrato = async () => {
    if (!client?.ultima_conversao_id) {
      toast.warn('Não há conversão da ClickSign vinculada a este tenant para recapturar.');
      return;
    }
    setIsRecapturando(client.ultima_conversao_id);
    try {
      await api.post(`/api/vendas/conversoes/${client.ultima_conversao_id}/recapturar-contrato`);
      toast.success('Contrato assinado recapturado com sucesso!');
      onSaveSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'A ClickSign ainda não disponibilizou o PDF assinado. Tente novamente em instantes.');
    } finally {
      setIsRecapturando(null);
    }
  };

  const handleViewContract = async () => {
    if (!client?.contrato_url) return;
    setIsLoadingPreview(true);
    try {
      const response = await api.get(client.contrato_url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(response.data);
      setContractPreviewUrl(blobUrl);
    } catch (err) {
      toast.error('Não foi possível carregar o contrato para visualização.');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleCloseContractPreview = () => {
    if (contractPreviewUrl) URL.revokeObjectURL(contractPreviewUrl);
    setContractPreviewUrl(null);
  };

  const handleDeleteContract = async () => {
    if (!window.confirm('Remover o contrato assinado deste tenant? Você poderá recapturar da ClickSign ou anexar um novo depois.')) return;
    setIsDeletingContract(true);
    try {
      await api.delete(`/api/system-hub/clientes/${client.id}/contrato`);
      toast.success('Contrato removido. Você já pode tentar novamente.');
      onSaveSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Falha ao remover o contrato.');
    } finally {
      setIsDeletingContract(false);
    }
  };

  const handleToggleStatus = async () => {
    const novoStatus = statusEmpresa === 'ativo' ? 'suspenso' : 'ativo';
    const label = novoStatus === 'suspenso' ? 'SUSPENDER' : 'REATIVAR';
    if (!window.confirm(`Confirma: ${label} o tenant "${client.nome_empresa}"?`)) return;

    setIsActioning(true);
    try {
      await api.put(`/api/system-hub/clientes/${client.id}`, { status: novoStatus });
      setStatusEmpresa(novoStatus);
      toast.success(`Tenant ${novoStatus === 'ativo' ? 'reativado' : 'suspenso'} com sucesso.`);
      onSaveSuccess?.();
    } catch {
      toast.error('Falha ao alterar status.');
    } finally {
      setIsActioning(false);
    }
  };

  const handleForceLogout = async () => {
    const confirmInput = window.prompt(
      `Essa ação desconecta TODOS os usuários de "${client.nome_empresa}" imediatamente.\n\nDigite o nome da empresa para confirmar:`,
    );
    if (confirmInput !== client.nome_empresa) {
      if (confirmInput !== null) toast.warn('Nome incorreto. Ação cancelada.');
      return;
    }

    setIsActioning(true);
    try {
      const { data } = await api.post(`/api/system-hub/clientes/${client.id}/force-logout-all`);
      toast.success(data.message || 'Sessões encerradas com sucesso.');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Falha ao encerrar sessões.');
    } finally {
      setIsActioning(false);
    }
  };

  const tabs = [
    { id: 'geral',       label: 'Visão Geral & Contatos', icon: <FaIdCard /> },
    { id: 'licencas',    label: 'Licenciamento',          icon: <FaKey /> },
    { id: 'faturamento', label: 'Faturamento',            icon: <FaFileInvoiceDollar /> },
    { id: 'whitelabel',  label: 'Domínio & White-Label',  icon: <FaPalette /> },
    { id: 'acoes',       label: 'Ações Críticas',         icon: <FaExclamationTriangle /> },
  ];

  return (
    <div className="sm-overlay" onMouseDown={onClose}>
      <div className="sm-dialog sm-dialog--expanded" onMouseDown={(e) => e.stopPropagation()}>

        <div className="sm-header">
          <div className="sm-header-left">
            <div className="sm-header-icon"><FaBuilding /></div>
            <div className="sm-header-content">
              <div className="sm-header-topline">
                <h2 className="sm-header-title">Configurações do Tenant</h2>
                <span className={`sm-status-badge sm-status-${statusEmpresa}`}>
                  {statusEmpresa === 'ativo' ? '● Ativo' : '● Suspenso'}
                </span>
              </div>
              <p className="sm-header-sub">{client.nome_empresa}</p>
              <div className="sm-pills-row">
                {headerMeta.map((item) => (
                  <InfoPill
                    key={item.label}
                    label={item.label}
                    value={item.value}
                    tone={
                      item.label === 'Status'
                        ? statusEmpresa === 'ativo' ? 'success' : 'danger'
                        : 'default'
                    }
                  />
                ))}
              </div>
            </div>
          </div>
          <button type="button" className="sm-close-btn" onClick={onClose} title="Fechar">&times;</button>
        </div>

        <div className="sm-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`sm-tab ${activeTab === tab.id ? 'sm-tab--active' : ''} ${tab.id === 'acoes' ? 'sm-tab--danger' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        <form id="sm-form" onSubmit={handleSave} className="sm-body sm-scrollless">
          {isLoadingConfig && activeTab !== 'faturamento' ? (
            <div className="sm-loading">
              <FaSpinner className="sm-spin" />
              <span>Carregando configurações...</span>
            </div>
          ) : (
            <>
              {activeTab === 'geral' && (
                <div className="sm-tab-pane">
                  <div className="sm-pane-grid sm-pane-grid--geral">
                    <section className="sm-section">
                      <div className="sm-section-title"><FaBuilding />Informações da Empresa</div>
                      <div className="sm-grid-3">
                        <div className="sm-field">
                          <label className="sm-field-label">Razão Social / Nome Fantasia</label>
                          <input className="sm-input" type="text" value={nomeEmpresa} onChange={(e) => setNomeEmpresa(e.target.value)} required />
                        </div>
                        <div className="sm-field">
                          <label className="sm-field-label">CNPJ</label>
                          <input className="sm-input" type="text" value={documentoEmpresa} onChange={(e) => setDocumentoEmpresa(e.target.value)} placeholder="00.000.000/0000-00" />
                        </div>
                        <div className="sm-field">
                          <label className="sm-field-label">Status Operacional</label>
                          <select className="sm-input sm-select" value={statusEmpresa} onChange={(e) => setStatusEmpresa(e.target.value)}>
                            <option value="ativo">Ativo</option>
                            <option value="suspenso">Suspenso</option>
                          </select>
                        </div>
                      </div>
                    </section>

                    <section className="sm-section">
                      <div className="sm-section-title"><FaFilePdf />Contrato Assinado</div>

                      {contratoAssinadoViaClicksign ? (
                        <div className="sm-contract-row sm-contract-row--signed">
                          <div className="sm-contract-icon-wrap sm-contract-icon-wrap--ok">
                            <FaCheckCircle className="sm-contract-icon" />
                          </div>
                          <div className="sm-contract-info">
                            <span className="sm-contract-label">
                              {'Assinado digitalmente via ClickSign'}
                              {client.contrato_assinado_em ? ` em ${formatDateBR(client.contrato_assinado_em)}` : ''}
                            </span>
                            <div className="sm-contract-actions-inline">
                              <button
                                type="button"
                                onClick={handleViewContract}
                                disabled={isLoadingPreview}
                                className="sm-btn sm-btn--outline sm-btn--sm"
                              >
                                {isLoadingPreview ? 'Carregando...' : 'Ver Contrato'}
                              </button>
                              <button
                                type="button"
                                onClick={handleDeleteContract}
                                disabled={isDeletingContract}
                                className="sm-btn sm-btn--danger sm-btn--sm"
                                title="Remove o contrato atual para permitir recapturar ou anexar outro"
                              >
                                <FaTrash size={11} />
                                {isDeletingContract ? 'Removendo...' : 'Excluir'}
                              </button>
                            </div>
                          </div>
                          <details className="sm-contract-manual-toggle">
                            <summary>Substituir manualmente</summary>
                            <input
                              type="file"
                              accept=".pdf,image/*"
                              className="sm-file-input"
                              onChange={(e) => setContratoFile(e.target.files?.[0] || null)}
                            />
                            {contratoFile && <span className="sm-contract-selected">📎 {contratoFile.name}</span>}
                          </details>
                        </div>
                      ) : (
                        <div className="sm-contract-row">
                          <div className="sm-contract-icon-wrap"><FaFilePdf className="sm-contract-icon" /></div>
                          <div className="sm-contract-info">
                            <span className="sm-contract-label">Anexar PDF / Imagem do contrato</span>
                            <input type="file" accept=".pdf,image/*" className="sm-file-input" onChange={(e) => setContratoFile(e.target.files?.[0] || null)} />
                            {contratoFile && <span className="sm-contract-selected">📎 {contratoFile.name}</span>}
                          </div>
                          {client.contrato_url && !contratoFile && (
                            <button
                              type="button"
                              onClick={handleViewContract}
                              disabled={isLoadingPreview}
                              className="sm-btn sm-btn--outline"
                            >
                              {isLoadingPreview ? 'Carregando...' : 'Ver Contrato Atual'}
                            </button>
                          )}
                          {client.ultima_conversao_id && (
                            <button
                              type="button"
                              className="sm-btn sm-btn--outline sm-btn--sm"
                              disabled={isRecapturando === client.ultima_conversao_id}
                              onClick={handleRecapturarContrato}
                              title="Tenta buscar novamente o PDF assinado na ClickSign"
                            >
                              {isRecapturando === client.ultima_conversao_id ? 'Buscando...' : 'Recapturar da ClickSign'}
                            </button>
                          )}
                        </div>
                      )}
                    </section>

                    <section className="sm-section sm-section--full">
                      <div className="sm-section-header">
                        <div className="sm-section-title sm-section-title--no-margin"><FaUserTie />Quadro de Contatos</div>
                        <button type="button" className="sm-btn sm-btn--primary sm-btn--sm" onClick={handleAddContato}><FaPlus />Novo Contato</button>
                      </div>
                      {contatos.length === 0 && <div className="sm-empty-state">Nenhum contato cadastrado.</div>}
                      <div className="sm-contacts-stack sm-scrollless">
                        {contatos.map((c, i) => (
                          <div key={c.id} className={`sm-contact-card ${c.is_principal ? 'sm-contact-card--principal' : ''} ${c.is_billing ? 'sm-contact-card--billing' : ''}`}>
                            <div className="sm-contact-head">
                              <div className="sm-contact-badges">
                                <label className="sm-checkbox">
                                  <input type="checkbox" checked={!!c.is_principal} onChange={(e) => handleChangeContato(i, 'is_principal', e.target.checked)} />
                                  <FaCheckCircle size={11} />Contato Principal
                                </label>
                                <label className="sm-checkbox">
                                  <input type="checkbox" checked={!!c.is_billing} onChange={(e) => handleChangeContato(i, 'is_billing', e.target.checked)} />
                                  <FaCheckCircle size={11} />Responsável Financeiro
                                </label>
                              </div>
                              <button type="button" className="sm-btn sm-btn--danger sm-btn--icon" onClick={() => handleRemoveContato(i, c.id)} title="Remover contato">
                                <FaTrash size={12} />
                              </button>
                            </div>
                            <div className="sm-grid-5">
                              <div className="sm-field">
                                <label className="sm-field-label">Nome Completo</label>
                                <input className="sm-input" type="text" value={c.nome} onChange={(e) => handleChangeContato(i, 'nome', e.target.value)} placeholder="João da Silva" />
                              </div>
                              <div className="sm-field">
                                <label className="sm-field-label">Cargo</label>
                                <select className="sm-input sm-select" value={c.cargo_tag} onChange={(e) => handleChangeContato(i, 'cargo_tag', e.target.value)}>
                                  {['Admin', 'Financeiro', 'Técnico', 'Comercial', 'Sócio', 'Jurídico'].map((o) => <option key={o} value={o}>{o}</option>)}
                                </select>
                              </div>
                              <div className="sm-field">
                                <label className="sm-field-label">E-mail</label>
                                <input className="sm-input" type="email" value={c.email} onChange={(e) => handleChangeContato(i, 'email', e.target.value)} placeholder="email@empresa.com" />
                              </div>
                              <div className="sm-field">
                                <label className="sm-field-label">WhatsApp</label>
                                <input className="sm-input" type="text" value={c.telefone} onChange={(e) => handleChangeContato(i, 'telefone', e.target.value)} placeholder="(11) 99999-9999" />
                              </div>
                              <div className="sm-field">
                                <label className="sm-field-label">CPF</label>
                                <input className="sm-input" type="text" value={c.documento} onChange={(e) => handleChangeContato(i, 'documento', e.target.value)} placeholder="000.000.000-00" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  </div>
                </div>
              )}

              {activeTab === 'licencas' && (
                <div className="sm-tab-pane">
                  <div className="sm-pane-grid sm-pane-grid--licencas">
                    <section className="sm-section">
                      <div className="sm-section-title"><FaKey />Controle de Licenças</div>
                      <div className="sm-license-summary">
                        <div className="sm-license-kpi sm-license-kpi--violet">
                          <span className="sm-license-kpi-value">{licEmUso}</span>
                          <span className="sm-license-kpi-label">Em uso</span>
                        </div>
                        <div className="sm-license-kpi">
                          <span className="sm-license-kpi-value">{maxLicencas}</span>
                          <span className="sm-license-kpi-label">Capacidade</span>
                        </div>
                        <div className="sm-license-kpi sm-license-kpi--green">
                          <span className="sm-license-kpi-value">{Math.max(0, maxLicencas - licEmUso)}</span>
                          <span className="sm-license-kpi-label">Disponíveis</span>
                        </div>
                      </div>
                      <LicenseBar used={licEmUso} total={maxLicencas} />
                      <div className="sm-field sm-license-adjust">
                        <label className="sm-field-label">Ajustar Capacidade Total</label>
                        <div className="sm-counter">
                          <button type="button" className="sm-counter-btn" onClick={() => setMaxLicencas((v) => Math.max(licEmUso, v - 1))}>−</button>
                          <input className="sm-counter-input" type="number" min={licEmUso} value={maxLicencas} onChange={(e) => setMaxLicencas(Math.max(licEmUso, parseInt(e.target.value, 10) || 1))} />
                          <button type="button" className="sm-counter-btn" onClick={() => setMaxLicencas((v) => v + 1)}>+</button>
                        </div>
                        {maxLicencas < licEmUso && <span className="sm-field-error">Mínimo {licEmUso} (em uso atualmente)</span>}
                      </div>
                    </section>
                    <section className="sm-section">
                      <div className="sm-section-title">Chaves Alocadas</div>
                      {licencasAlocadas.length === 0 ? (
                        <div className="sm-empty-state">Nenhuma licença encontrada.</div>
                      ) : (
                        <div className="sm-grid-2 sm-scrollless">
                          {licencasAlocadas.map((lic) => (
                            <div key={lic.id} className="sm-license-row">
                              <code className="sm-license-key">{lic.chave_licenca}</code>
                              {lic.usuario_id_alocado ? (
                                <div className="sm-license-user">
                                  <span className="sm-license-user-name">{lic.usuario_nome}</span>
                                  <span className="sm-license-user-email">{lic.usuario_email}</span>
                                </div>
                              ) : (
                                <span className="sm-license-free">● Disponível</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </section>
                  </div>
                </div>
              )}

              {activeTab === 'faturamento' && (
                <div className="sm-tab-pane">
                  <BillingSettingsTab clienteId={client.id} />
                </div>
              )}

              {activeTab === 'whitelabel' && (
                <div className="sm-tab-pane">
                  <div className="sm-info-banner">
                    <FaPalette />
                    <span>
                      Estes dados alimentam o <strong>Dashboard</strong> e a{' '}
                      <strong>Sidebar</strong> dos usuários desta empresa em tempo real. Salve para aplicar as alterações.
                    </span>
                  </div>

                  <div className="sm-wl-layout sm-wl-layout--expanded">
                    <div className="sm-wl-col-text">
                      <section className="sm-section">
                        <div className="sm-section-title"><FaGlobe />URL de Login</div>
                        <div className="sm-field">
                          <label className="sm-field-label">Identificador de URL (Slug)</label>
                          <div className="sm-slug-wrap">
                            <span className="sm-slug-prefix">{window.location.origin}/login/</span>
                            <input className="sm-slug-input" type="text" value={slug} onChange={(e) => setSlug(formatSlug(e.target.value))} placeholder="nome-da-empresa" />
                          </div>
                          <span className="sm-field-hint">Cria a URL de login exclusiva para este cliente.</span>
                        </div>
                      </section>

                      <section className="sm-section">
                        <div className="sm-section-title">📊 Textos do Dashboard</div>
                        <div className="sm-field">
                          <label className="sm-field-label"><FaBuilding size={11} />Nome de Exibição da Empresa</label>
                          <input className="sm-input" type="text" value={sidebarLabel} onChange={(e) => setSidebarLabel(e.target.value)} placeholder="Ex: Guincho Oliveira Transportes" maxLength={80} />
                          <span className="sm-field-hint">Aparece no subtítulo "Bem-vindo ao seu dia na empresa" e na Sidebar.</span>
                        </div>
                        <div className="sm-field">
                          <label className="sm-field-label"><FaHeading size={11} />Título da Seção Institucional</label>
                          <input className="sm-input" type="text" value={dashboardTitle} onChange={(e) => setDashboardTitle(e.target.value)} placeholder="Ex: Sobre a Guincho Oliveira" maxLength={100} />
                        </div>
                        <div className="sm-field">
                          <label className="sm-field-label"><FaAlignLeft size={11} />Texto Institucional</label>
                          <textarea className="sm-input sm-textarea" rows={5} value={dashboardText} onChange={(e) => setDashboardText(e.target.value)} placeholder="Descreva brevemente a empresa..." maxLength={500} />
                          <div className="sm-char-count"><span>{dashboardText.length}/500 caracteres</span></div>
                        </div>
                      </section>
                    </div>

                    <div className="sm-wl-col-images">
                      <section className="sm-section">
                        <div className="sm-section-title">🎨 Identidade Visual</div>

                        <ImageUploadBox
                          label="Logo da Empresa (Sidebar / Login)"
                          hint="Aparece na Sidebar e na tela de login. PNG transparente recomendado."
                          existingUrl={clearLogo ? null : existingLogoUrl}
                          selectedFile={logoFile}
                          onSelect={(f) => { setLogoFile(f); setClearLogo(false); }}
                          onClear={() => { setLogoFile(null); setClearLogo(true); setExistingLogoUrl(null); }}
                        />

                        <ImageUploadBox
                          label="Fundo da Tela de Login"
                          hint="Imagem exibida no painel direito da tela de login do cliente."
                          existingUrl={clearLoginBg ? null : existingLoginBg}
                          selectedFile={loginBgFile}
                          onSelect={(f) => { setLoginBgFile(f); setClearLoginBg(false); }}
                          onClear={() => { setLoginBgFile(null); setClearLoginBg(true); setExistingLoginBg(null); }}
                        />
                      </section>

                      <section className="sm-section sm-preview-panel">
                        <div className="sm-section-title">✨ Prévia de Aparência</div>

                        <div className="sm-preview-shell">
                          <aside className="sm-preview-sidebar">
                            <div className="sm-preview-logo">
                              {displayLogoSrc ? (
                                <img
                                  src={displayLogoSrc}
                                  alt={previewCompanyName}
                                  className="sm-preview-logo-img"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentNode.querySelector('.sm-preview-logo-fallback')?.removeAttribute('style');
                                  }}
                                  style={{ maxWidth: '80%', maxHeight: '52px', objectFit: 'contain', display: 'block', margin: '0 auto' }}
                                />
                              ) : null}
                              <div
                                className="sm-preview-logo-box sm-preview-logo-fallback"
                                style={displayLogoSrc ? { display: 'none' } : undefined}
                              >
                                LOGO
                              </div>
                            </div>

                            <div className="sm-preview-company">{previewCompanyName}</div>
                            <div className="sm-preview-menu">
                              <span>Dashboard</span>
                              <span>Ordens de Serviço</span>
                              <span>Financeiro</span>
                              <span>Relatórios</span>
                            </div>
                          </aside>

                          <div className="sm-preview-main">
                            <div
                              className="sm-preview-hero"
                              style={
                                displayBgSrc
                                  ? {
                                      backgroundImage: `url(${displayBgSrc})`,
                                      backgroundSize: 'cover',
                                      backgroundPosition: 'center',
                                    }
                                  : { background: 'linear-gradient(135deg, #eef2ff 0%, #f8fafc 100%)' }
                              }
                            >
                              <div className="sm-preview-hero-badge">Dashboard</div>
                              <h4>{previewDashboardTitle}</h4>
                              <p>{previewDashboardText}</p>
                            </div>
                          </div>
                        </div>
                      </section>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'acoes' && (
                <div className="sm-tab-pane">
                  <div className="sm-danger-banner">
                    <FaExclamationTriangle />
                    <div>
                      <strong>Zona de Perigo</strong>
                      <p>As ações abaixo têm efeito imediato e não podem ser desfeitas automaticamente.</p>
                    </div>
                  </div>

                  {/* ══ [TRIAL] Período de Teste do CLIENTE (maior decisor) ══════ */}
                  <div className="sm-trial-card">
                    <div className="sm-trial-head">
                      <div className="sm-trial-title">
                        <FaHourglassHalf />
                        <div>
                          <h4>Período de Teste (Trial) do Cliente</h4>
                          <p>
                            Quando ativo, <strong>todos os usuários deste tenant</strong> ficam em teste e são
                            bloqueados automaticamente no horário definido (horário de Brasília). Este controle
                            <strong> prevalece sobre o trial individual</strong> de cada usuário.
                          </p>
                        </div>
                      </div>
                      <label className="sm-trial-switch">
                        <input
                          type="checkbox"
                          checked={trialEnabled}
                          onChange={(e) => setTrialEnabled(e.target.checked)}
                        />
                        <span>{trialEnabled ? 'Ativado' : 'Desativado'}</span>
                      </label>
                    </div>

                    {trialEnabled && (
                      <div className="sm-trial-body">
                        <label className="sm-label">Término do período de teste (data e hora — horário de Brasília)</label>
                        <input
                          type="datetime-local"
                          className="sm-input"
                          value={trialExpiresLocal}
                          onChange={(e) => setTrialExpiresLocal(e.target.value)}
                          min={utcToSpInput(new Date().toISOString())}
                        />
                        <div className="sm-trial-preview">
                          <TrialCountdown expiresAt={spInputToUtcIso(trialExpiresLocal)} label="Prévia do contador" />
                        </div>
                        <p className="sm-trial-hint">
                          As alterações são aplicadas ao clicar em <strong>Salvar Alterações</strong>. Enquanto este
                          trial estiver ativo, o trial individual dos usuários deste tenant fica bloqueado na tela de
                          Usuários &amp; Permissões.
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="sm-danger-grid sm-danger-grid--wide">
                    <div className={`sm-danger-card ${statusEmpresa === 'ativo' ? 'sm-danger-card--warn' : 'sm-danger-card--ok'}`}>
                      <div className="sm-danger-card-icon">{statusEmpresa === 'ativo' ? <FaLock /> : <FaUnlock />}</div>
                      <div className="sm-danger-card-info">
                        <h4>{statusEmpresa === 'ativo' ? 'Suspender Operação' : 'Reativar Operação'}</h4>
                        <p>{statusEmpresa === 'ativo' ? 'Bloqueia o acesso de todos os funcionários deste tenant imediatamente.' : 'Restaura o acesso completo ao sistema para este tenant.'}</p>
                      </div>
                      <button type="button" className={`sm-btn ${statusEmpresa === 'ativo' ? 'sm-btn--danger' : 'sm-btn--success'}`} onClick={handleToggleStatus} disabled={isActioning}>
                        {isActioning ? <FaSpinner className="sm-spin" /> : statusEmpresa === 'ativo' ? 'Suspender' : 'Reativar'}
                      </button>
                    </div>

                    <div className="sm-danger-card sm-danger-card--neutral">
                      <div className="sm-danger-card-icon"><FaSignOutAlt /></div>
                      <div className="sm-danger-card-info">
                        <h4>Forçar Desconexão Global</h4>
                        <p>Encerra ativamente a sessão de todos os usuários conectados agora. Eles serão redirecionados para o login.</p>
                      </div>
                      <button type="button" className="sm-btn sm-btn--dark" onClick={handleForceLogout} disabled={isActioning}>
                        {isActioning ? <FaSpinner className="sm-spin" /> : 'Logoff Global'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </form>

        {/* Lightbox de preview do contrato assinado */}
        {contractPreviewUrl && (
          <div className="sm-contract-preview-overlay" onClick={handleCloseContractPreview}>
            <div className="sm-contract-preview-box" onClick={(e) => e.stopPropagation()}>
              <div className="sm-contract-preview-header">
                <span><FaFilePdf /> Contrato Assinado</span>
                <button type="button" className="sm-close-btn" onClick={handleCloseContractPreview} title="Fechar">
                  &times;
                </button>
              </div>
              <iframe
                src={contractPreviewUrl}
                title="Contrato assinado"
                className="sm-contract-preview-frame"
              />
            </div>
          </div>
        )}

        {!isFaturamentoTab && (
          <div className="sm-footer">
            <button type="button" className="sm-btn sm-btn--outline" onClick={onClose} disabled={isSaving}>Cancelar</button>
            <button type="submit" form="sm-form" className="sm-btn sm-btn--primary" disabled={isSaving || isLoadingConfig || maxLicencas < licEmUso}>
              {isSaving ? (<><FaSpinner className="sm-spin" />Salvando...</>) : (<><FaSave />Salvar Alterações</>)}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}